import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Grid,
  Tabs,
  Tab,
  Chip,
  Fade,
  useTheme,
  useMediaQuery,
  Stack,
  Card,
  CardContent,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Backdrop,
  CircularProgress,
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import EditIcon from '@mui/icons-material/Edit';
import CancelIcon from '@mui/icons-material/Cancel';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';

import { fetchInvoiceById, updateInvoice, fetchInvoices } from '../../store/slices/invoiceSlice';
import { RootState, AppDispatch } from '../../store/store';
import { Invoice, InvoiceFormData, InvoiceItem } from '../../types/invoice';
import PDFDownload from '../PDF/PDFDownload';
import PDFViewer from '../PDF/PDFViewer';
import ISDOCDownload from '../ISDOC/ISDOCDownload';
import InvoiceItems from './InvoiceItems';
import { cs } from '../../translations/cs';
import czechTranslations from '../../translations/czech';
import { formatDateForInput } from '../../utils/dateUtils';

const statusColors = {
  new: 'default',
  pending: 'warning',
  reviewed: 'info',
  ready_for_payment: 'primary',
  sent_to_bank: 'secondary',
  paid: 'success',
  paid_in_cash: 'success',
  rejected: 'error',
  cancelled: 'error',
} as const;

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      style={{ minHeight: value === index ? 'auto' : 0 }} // Ensure consistent container sizing
      {...other}
    >
      {value === index && (
        <Box sx={{ 
          p: 3,
          minHeight: '400px', // Minimum height to prevent content jumping
          position: 'relative'
        }}>
          {children}
        </Box>
      )}
    </div>
  );
}

const SimpleInvoiceDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { currentInvoice, loading, error } = useSelector((state: RootState) => state.invoices);
  const { user } = useSelector((state: RootState) => state.auth);

  const [tabValue, setTabValue] = useState(0);
  const [isEditing, setIsEditing] = useState(location.pathname.includes('/edit'));
  const [formData, setFormData] = useState<InvoiceFormData | null>(null);
  const [currentItems, setCurrentItems] = useState<InvoiceItem[]>([]);
  const [pdfDialogOpen, setPdfDialogOpen] = useState(false);

  // Determine where to navigate back based on location state
  const getBackPath = () => {
    const state = location.state as any;
    if (state?.from === 'abo' && state?.aboId) {
      return `/abo/${state.aboId}`;
    }
    return '/invoices';
  };

  // Load invoice
  useEffect(() => {
    if (id && id !== 'new') {
      dispatch(fetchInvoiceById(parseInt(id)));
    }
  }, [dispatch, id]);

  // Initialize form data when invoice loads
  useEffect(() => {
    if (currentInvoice && currentInvoice.id) {
      const invoiceItems = currentInvoice.items || [];
      
      setFormData({
        invoiceNumber: currentInvoice.invoiceNumber || '',
        invoiceDate: currentInvoice.invoiceDate || '',
        dueDate: currentInvoice.dueDate || '',
        vatRenderedDate: currentInvoice.vatRenderedDate || undefined,
        supplierName: currentInvoice.supplierName || '',
        supplierAddress: currentInvoice.supplierAddress || '',
        supplierICO: currentInvoice.supplierICO || '',
        supplierDIC: currentInvoice.supplierDIC || '',
        supplierBankAccount: currentInvoice.supplierBankAccount || '',

        supplierIBAN: currentInvoice.supplierIBAN || '',
        supplierSWIFT: currentInvoice.supplierSWIFT || '',
        customerName: currentInvoice.customerName || '',
        customerAddress: currentInvoice.customerAddress || '',
        customerICO: currentInvoice.customerICO || '',
        customerDIC: currentInvoice.customerDIC || '',
        customerBankAccount: currentInvoice.customerBankAccount || '',

        customerIBAN: currentInvoice.customerIBAN || '',
        customerSWIFT: currentInvoice.customerSWIFT || '',
        subtotal: currentInvoice.subtotal || 0,
        vatAmount: currentInvoice.vatAmount || 0,
        totalAmount: currentInvoice.totalAmount || 0,
        currency: currentInvoice.currency || 'CZK',
        vatRate: currentInvoice.vatRate || 21,
        status: currentInvoice.status || 'new',
        processingStatus: currentInvoice.processingStatus || 'new',
        assignedToId: currentInvoice.assignedToId,
        notes: currentInvoice.notes || '',
        items: invoiceItems
      });
      
      setCurrentItems([...invoiceItems]);
    }
  }, [currentInvoice?.id]);

  const getStatusLabel = (status: string): string => {
    return czechTranslations.invoices.status[status as keyof typeof czechTranslations.invoices.status] || status;
  };

  const getStatusColor = (status: string) => {
    return statusColors[status as keyof typeof statusColors] || 'default';
  };

  const handleInputChange = (field: keyof InvoiceFormData, value: any) => {
    if (formData) {
      setFormData({
        ...formData,
        [field]: value
      });
    }
  };

  const handleItemsChange = (items: InvoiceItem[]) => {
    setCurrentItems(items);
  };

  const handleSave = async () => {
    if (formData && id) {
      try {
        const dataToSave = {
          ...formData,
          items: currentItems
        };
        await dispatch(updateInvoice({ id: parseInt(id), data: dataToSave })).unwrap();
        
        // Refresh the current invoice data to ensure it's up to date
        await dispatch(fetchInvoiceById(parseInt(id)));
        
        setIsEditing(false);
      } catch (error) {
        console.error('Error saving invoice:', error);
      }
    }
  };

  const handleEdit = () => {
    setIsEditing(true);
  };

  const handleCancel = () => {
    if (currentInvoice) {
      setFormData({
        invoiceNumber: currentInvoice.invoiceNumber || '',
        invoiceDate: currentInvoice.invoiceDate || '',
        dueDate: currentInvoice.dueDate || '',
        vatRenderedDate: currentInvoice.vatRenderedDate || undefined,
        supplierName: currentInvoice.supplierName || '',
        supplierAddress: currentInvoice.supplierAddress || '',
        supplierICO: currentInvoice.supplierICO || '',
        supplierDIC: currentInvoice.supplierDIC || '',
        supplierBankAccount: currentInvoice.supplierBankAccount || '',

        supplierIBAN: currentInvoice.supplierIBAN || '',
        supplierSWIFT: currentInvoice.supplierSWIFT || '',
        customerName: currentInvoice.customerName || '',
        customerAddress: currentInvoice.customerAddress || '',
        customerICO: currentInvoice.customerICO || '',
        customerDIC: currentInvoice.customerDIC || '',
        customerBankAccount: currentInvoice.customerBankAccount || '',

        customerIBAN: currentInvoice.customerIBAN || '',
        customerSWIFT: currentInvoice.customerSWIFT || '',
        subtotal: currentInvoice.subtotal || 0,
        vatAmount: currentInvoice.vatAmount || 0,
        totalAmount: currentInvoice.totalAmount || 0,
        currency: currentInvoice.currency || 'CZK',
        vatRate: currentInvoice.vatRate || 21,
        status: currentInvoice.status || 'new',
        processingStatus: currentInvoice.processingStatus || 'new',
        assignedToId: currentInvoice.assignedToId,
        notes: currentInvoice.notes || '',
        items: currentInvoice.items || []
      });
      setCurrentItems(currentInvoice.items || []);
    }
    setIsEditing(false);
  };

  if (loading) {
    return (
      <Backdrop open={true} sx={{ zIndex: theme.zIndex.drawer + 1, color: '#fff' }}>
        <CircularProgress color="inherit" />
      </Backdrop>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="h6" color="error">
          Chyba: {error}
        </Typography>
      </Box>
    );
  }

  if (!currentInvoice || !formData) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="h6">
          Faktura nebyla nalezena
        </Typography>
      </Box>
    );
  }

  return (
    <Fade in={true} timeout={600}>
      <Box sx={{ p: { xs: 1, sm: 2, md: 3 } }}>
        {/* Header */}
        <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Button
              startIcon={<ArrowBackIcon />}
              onClick={() => navigate(getBackPath())}
              variant="outlined"
              size="small"
            >
              Zpět
            </Button>
            <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
              {formData.invoiceNumber}
            </Typography>
            <Chip
              label={getStatusLabel(formData.status)}
              color={getStatusColor(formData.status)}
              size="small"
            />
          </Box>
          
          <Stack direction="row" spacing={1}>
            {currentInvoice.pdfData && (
              <PDFDownload 
                invoiceId={currentInvoice.id!} 
                invoiceNumber={currentInvoice.invoiceNumber}
              />
            )}
            <ISDOCDownload 
              invoiceId={currentInvoice.id!} 
              invoiceNumber={currentInvoice.invoiceNumber}
            />
            {isEditing ? (
              <>
                <Button
                  variant="contained"
                  startIcon={<SaveIcon />}
                  onClick={handleSave}
                  color="primary"
                >
                  Uložit
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<CancelIcon />}
                  onClick={handleCancel}
                >
                  Zrušit
                </Button>
              </>
            ) : (
              <Button
                variant="contained"
                startIcon={<EditIcon />}
                onClick={handleEdit}
                color="primary"
              >
                Upravit
              </Button>
            )}
          </Stack>
        </Box>

        {/* Tabs */}
        <Paper sx={{ width: '100%' }}>
          <Box sx={{ 
            borderBottom: 1, 
            borderColor: 'divider',
            minHeight: 48,  // Fixed height for tab container
            display: 'flex',
            alignItems: 'center'
          }}>
            <Tabs 
              value={tabValue} 
              onChange={(_, value) => setTabValue(value)} 
              variant={isMobile ? "scrollable" : "standard"}
              scrollButtons={isMobile ? "auto" : false}
              allowScrollButtonsMobile
              sx={{
                width: '100%',
                '& .MuiTab-root': {
                  minWidth: 'auto',
                  padding: '12px 16px',
                  fontSize: '0.875rem',
                  fontWeight: 500,
                  textTransform: 'none',
                  transition: 'none', // Remove hover transitions that cause movement
                  '&:hover': {
                    backgroundColor: 'rgba(0, 0, 0, 0.04)',
                    transition: 'none'
                  }
                },
                '& .MuiTabs-indicator': {
                  transition: 'left 300ms cubic-bezier(0.4, 0, 0.2, 1), width 300ms cubic-bezier(0.4, 0, 0.2, 1)'
                }
              }}
            >
              <Tab label={cs.invoice.tabs?.basicInfo || "Základní údaje"} />
              <Tab label={cs.invoice.tabs?.supplier || "Dodavatel"} />
              <Tab label={cs.invoice.tabs?.customer || "Odběratel"} />
              <Tab label={cs.invoice.tabs?.items || "Položky"} />
              {currentInvoice.pdfData && <Tab label={cs.invoice.tabs?.documents || "Dokumenty"} />}
            </Tabs>
          </Box>

          {/* Basic Info Tab */}
          <TabPanel value={tabValue} index={0}>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>Informace o faktuře</Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          label="Číslo faktury"
                          value={formData.invoiceNumber}
                          onChange={(e) => handleInputChange('invoiceNumber', e.target.value)}
                          disabled={!isEditing}
                        />
                      </Grid>
                      <Grid item xs={6}>
                        <TextField
                          fullWidth
                          type="date"
                          label="Datum vystavení"
                          value={formatDateForInput(formData.invoiceDate)}
                          onChange={(e) => handleInputChange('invoiceDate', e.target.value)}
                          disabled={!isEditing}
                          InputLabelProps={{ shrink: true }}
                        />
                      </Grid>
                      <Grid item xs={6}>
                        <TextField
                          fullWidth
                          type="date"
                          label="Datum splatnosti"
                          value={formatDateForInput(formData.dueDate)}
                          onChange={(e) => handleInputChange('dueDate', e.target.value)}
                          disabled={!isEditing}
                          InputLabelProps={{ shrink: true }}
                        />
                      </Grid>
                      <Grid item xs={6}>
                        <TextField
                          fullWidth
                          type="date"
                          label="Datum vyúčtování DPH"
                          value={formatDateForInput(formData.vatRenderedDate)}
                          onChange={(e) => handleInputChange('vatRenderedDate', e.target.value || undefined)}
                          disabled={!isEditing}
                          InputLabelProps={{ shrink: true }}
                        />
                      </Grid>
                      <Grid item xs={6}>
                        <FormControl fullWidth disabled={!isEditing}>
                          <InputLabel>Stav</InputLabel>
                          <Select
                            value={formData.status}
                            onChange={(e) => handleInputChange('status', e.target.value)}
                            label="Stav"
                          >
                            {Object.keys(czechTranslations.invoices.status).map((key) => (
                              <MenuItem key={key} value={key}>{getStatusLabel(key)}</MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Grid>
                      <Grid item xs={6}>
                        <TextField
                          fullWidth
                          label="Měna"
                          value={formData.currency}
                          onChange={(e) => handleInputChange('currency', e.target.value)}
                          disabled={!isEditing}
                        />
                      </Grid>
                      <Grid item xs={6}>
                        <TextField
                          fullWidth
                          label="Sazba DPH (%)"
                          type="number"
                          value={formData.vatRate}
                          onChange={(e) => handleInputChange('vatRate', parseFloat(e.target.value) || 0)}
                          disabled={!isEditing}
                          inputProps={{ min: 0, max: 100, step: 0.01 }}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          label="Poznámky"
                          value={formData.notes || ''}
                          onChange={(e) => handleInputChange('notes', e.target.value)}
                          disabled={!isEditing}
                          multiline
                          rows={3}
                        />
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              </Grid>
              
              <Grid item xs={12} md={6}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>Přiřazená účetní jednotka</Typography>
                    {currentInvoice.businessUnit ? (
                      <Box>
                        <Typography variant="h6" color="primary" sx={{ fontWeight: 'bold', mb: 2 }}>
                          {currentInvoice.businessUnit.name}
                        </Typography>
                        <Grid container spacing={2}>
                          {currentInvoice.businessUnit.ico && (
                            <Grid item xs={6}>
                              <Typography variant="body2" color="textSecondary">
                                IČO: {currentInvoice.businessUnit.ico}
                              </Typography>
                            </Grid>
                          )}
                          {currentInvoice.businessUnit.dic && (
                            <Grid item xs={6}>
                              <Typography variant="body2" color="textSecondary">
                                DIČ: {currentInvoice.businessUnit.dic}
                              </Typography>
                            </Grid>
                          )}
                          {currentInvoice.businessUnit.address && (
                            <Grid item xs={12}>
                              <Typography variant="body2" color="textSecondary">
                                Adresa: {currentInvoice.businessUnit.address}
                              </Typography>
                            </Grid>
                          )}
                        </Grid>
                      </Box>
                    ) : (
                      <Box sx={{ textAlign: 'center', py: 2 }}>
                        <Typography variant="body1" color="textSecondary" sx={{ mb: 2 }}>
                          Faktura není přiřazena k žádné firmě
                        </Typography>
                        <Button
                          variant="outlined"
                          color="primary"
                          size="small"
                          onClick={() => {
                            // TODO: Open company assignment dialog
                        
                          }}
                        >
                          Přiřadit firmu
                        </Button>
                      </Box>
                    )}
                  </CardContent>
                </Card>
              </Grid>
              
              <Grid item xs={12} md={6}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>Celková částka</Typography>
                    <Typography variant="h4" color="primary" sx={{ fontWeight: 'bold', mb: 2 }}>
                      {formData.totalAmount?.toFixed(2)} {formData.currency}
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          label="Částka bez DPH"
                          type="number"
                          value={formData.subtotal}
                          onChange={(e) => handleInputChange('subtotal', parseFloat(e.target.value) || 0)}
                          disabled={!isEditing}
                          inputProps={{ min: 0, step: 0.01 }}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          label="Částka DPH"
                          type="number"
                          value={formData.vatAmount}
                          onChange={(e) => handleInputChange('vatAmount', parseFloat(e.target.value) || 0)}
                          disabled={!isEditing}
                          inputProps={{ min: 0, step: 0.01 }}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          label="Celková částka"
                          type="number"
                          value={formData.totalAmount}
                          onChange={(e) => handleInputChange('totalAmount', parseFloat(e.target.value) || 0)}
                          disabled={!isEditing}
                          inputProps={{ min: 0, step: 0.01 }}
                        />
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </TabPanel>

          {/* Supplier Tab */}
          <TabPanel value={tabValue} index={1}>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>{cs.invoice.supplierInfo}</Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={12} md={6}>
                        <TextField
                          fullWidth
                          label="Název dodavatele"
                          value={formData.supplierName}
                          onChange={(e) => handleInputChange('supplierName', e.target.value)}
                          disabled={!isEditing}
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <TextField
                          fullWidth
                          label="IČO dodavatele"
                          value={formData.supplierICO}
                          onChange={(e) => handleInputChange('supplierICO', e.target.value)}
                          disabled={!isEditing}
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <TextField
                          fullWidth
                          label="DIČ dodavatele"
                          value={formData.supplierDIC}
                          onChange={(e) => handleInputChange('supplierDIC', e.target.value)}
                          disabled={!isEditing}
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <TextField
                          fullWidth
                          label="Adresa dodavatele"
                          value={formData.supplierAddress}
                          onChange={(e) => handleInputChange('supplierAddress', e.target.value)}
                          disabled={!isEditing}
                          multiline
                          rows={2}
                        />
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>{cs.invoice.supplierBankInfo}</Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={12} md={6}>
                        <TextField
                          fullWidth
                          label={cs.invoice.bankAccount}
                          value={formData.supplierBankAccount}
                          onChange={(e) => handleInputChange('supplierBankAccount', e.target.value)}
                          disabled={!isEditing}
                        />
                      </Grid>

                      <Grid item xs={12} md={6}>
                        <TextField
                          fullWidth
                          label="IBAN"
                          value={formData.supplierIBAN}
                          onChange={(e) => handleInputChange('supplierIBAN', e.target.value)}
                          disabled={!isEditing}
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <TextField
                          fullWidth
                          label="SWIFT"
                          value={formData.supplierSWIFT}
                          onChange={(e) => handleInputChange('supplierSWIFT', e.target.value)}
                          disabled={!isEditing}
                        />
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </TabPanel>

          {/* Customer Tab */}
          <TabPanel value={tabValue} index={2}>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>Informace o odběrateli</Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={12} md={6}>
                        <TextField
                          fullWidth
                          label="Název odběratele"
                          value={formData.customerName}
                          onChange={(e) => handleInputChange('customerName', e.target.value)}
                          disabled={!isEditing}
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <TextField
                          fullWidth
                          label="IČO odběratele"
                          value={formData.customerICO}
                          onChange={(e) => handleInputChange('customerICO', e.target.value)}
                          disabled={!isEditing}
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <TextField
                          fullWidth
                          label="DIČ odběratele"
                          value={formData.customerDIC}
                          onChange={(e) => handleInputChange('customerDIC', e.target.value)}
                          disabled={!isEditing}
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <TextField
                          fullWidth
                          label="Adresa odběratele"
                          value={formData.customerAddress}
                          onChange={(e) => handleInputChange('customerAddress', e.target.value)}
                          disabled={!isEditing}
                          multiline
                          rows={2}
                        />
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>Bankovní údaje odběratele</Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={12} md={6}>
                        <TextField
                          fullWidth
                          label={cs.invoice.bankAccount}
                          value={formData.customerBankAccount}
                          onChange={(e) => handleInputChange('customerBankAccount', e.target.value)}
                          disabled={!isEditing}
                        />
                      </Grid>

                      <Grid item xs={12} md={6}>
                        <TextField
                          fullWidth
                          label="IBAN"
                          value={formData.customerIBAN}
                          onChange={(e) => handleInputChange('customerIBAN', e.target.value)}
                          disabled={!isEditing}
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <TextField
                          fullWidth
                          label="SWIFT"
                          value={formData.customerSWIFT}
                          onChange={(e) => handleInputChange('customerSWIFT', e.target.value)}
                          disabled={!isEditing}
                        />
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </TabPanel>

          {/* Items Tab */}
          <TabPanel value={tabValue} index={3}>
            <InvoiceItems
              items={currentItems}
              isEditing={isEditing}
              onItemsChange={handleItemsChange}
            />
          </TabPanel>

          {/* Documents Tab */}
          {currentInvoice.pdfData && (
            <TabPanel value={tabValue} index={4}>
              <Grid container spacing={3}>
                <Grid item xs={12} md={8}>
                  <Card>
                    <CardContent>
                      <Typography variant="h6" gutterBottom>PDF náhled</Typography>
              <PDFViewer
                invoiceId={currentInvoice.id!}
                invoiceNumber={currentInvoice.invoiceNumber}
              />
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Card>
                    <CardContent>
                      <Typography variant="h6" gutterBottom>Informace o dokumentech</Typography>
                      <Grid container spacing={2}>
                        {currentInvoice.pdfFilename && (
                          <Grid item xs={12}>
                            <Typography variant="body2" color="text.secondary">
                              <strong>PDF soubor:</strong> {currentInvoice.pdfFilename}
                            </Typography>
                          </Grid>
                        )}
                        {currentInvoice.pdfPath && (
                          <Grid item xs={12}>
                            <Typography variant="body2" color="text.secondary">
                              <strong>Cesta k PDF:</strong> {currentInvoice.pdfPath}
                            </Typography>
                          </Grid>
                        )}
                        {currentInvoice.isdocPath && (
                          <Grid item xs={12}>
                            <Typography variant="body2" color="text.secondary">
                              <strong>ISDOC cesta:</strong> {currentInvoice.isdocPath}
                            </Typography>
                          </Grid>
                        )}
                        <Grid item xs={12}>
                          <PDFDownload 
                            invoiceId={currentInvoice.id!}
                            invoiceNumber={currentInvoice.invoiceNumber}
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <ISDOCDownload 
                            invoiceId={currentInvoice.id!}
                            invoiceNumber={currentInvoice.invoiceNumber}
                          />
                        </Grid>
                      </Grid>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
            </TabPanel>
          )}
        </Paper>
      </Box>
    </Fade>
  );
};

export default SimpleInvoiceDetail;
